import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { TextBox } from '../../../components/base';
import { CKEditor } from '../../../components/base/CKEditor';
import { ProductModel } from '../../../context/models/products/ProductModel';

export type BulkUpdateProductDraft = Record<
	number,
	{
		price?: number;
		priceOnSale?: number;
		summary?: string;
	}
>;

type IBulkUpdateProductModalProps = {
	open: boolean;
	products: ProductModel[];
	isLoading?: boolean;
	isSubmitting?: boolean;
	progressText?: string;
	onClose: () => void;
	onApply: (draft: BulkUpdateProductDraft) => void;
};

const emptyDraft = (products: ProductModel[]) => {
	return products.reduce<BulkUpdateProductDraft>((accumulator, product) => {
		accumulator[product.id] = {
			price: product.price,
			priceOnSale: product.priceOnSale,
			summary: product.summary,
		};
		return accumulator;
	}, {});
};

const normalizeSummaryHtml = (value: string) => {
	const raw = value || '';
	if (!raw.trim()) return '';

	const textOnly = raw
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/\s+/g, '')
		.trim();

	const hasMeaningfulMarkup = /<(img|table|iframe|video|ul|ol|li|blockquote|h[1-6]|hr|pre|code|figure)\b/i.test(raw);

	return !textOnly && !hasMeaningfulMarkup ? '' : raw;
};

export const BulkUpdateProductModal = ({ open, products, isLoading = false, isSubmitting = false, progressText, onClose, onApply }: IBulkUpdateProductModalProps) => {
	const [drafts, setDrafts] = useState<BulkUpdateProductDraft>({});
	const [expandedProductIds, setExpandedProductIds] = useState<number[]>([]);

	const sortedProducts = useMemo(() => products.slice().sort((left, right) => left.id - right.id), [products]);

	useEffect(() => {
		if (!open) return;
		setDrafts(emptyDraft(sortedProducts));
		setExpandedProductIds(sortedProducts.slice(0, 3).map((product) => product.id));
	}, [open, sortedProducts]);

	const updateDraft = (productId: number, key: 'price' | 'priceOnSale' | 'summary', value: number | string) => {
		const nextValue = typeof value === 'string' ? value : value;
		setDrafts((current) => ({
			...current,
			[productId]: {
				...(current[productId] || {}),
				[key]: key === 'summary' ? String(nextValue ?? '') : nextValue === '' ? undefined : nextValue,
			},
		}));
	};

	const handleApply = () => {
		const normalizedDrafts = Object.entries(drafts).reduce<BulkUpdateProductDraft>((accumulator, [productId, draft]) => {
			const numericId = Number(productId);
			accumulator[numericId] = {
				price: draft.price,
				priceOnSale: draft.priceOnSale,
				summary: normalizeSummaryHtml(draft.summary || ''),
			};
			return accumulator;
		}, {});
		onApply(normalizedDrafts);
	};

	return (
		<Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
			<DialogContent className='flex max-h-[90vh] w-[calc(100vw-1rem)] max-w-6xl flex-col overflow-hidden rounded-2xl p-0 sm:w-[calc(100vw-3rem)]'>
				<DialogHeader className='border-b bg-muted/20 px-4 py-3 sm:px-6'>
					<DialogTitle className='text-lg font-semibold'>Cập nhật hàng loạt sản phẩm</DialogTitle>
					<p className='text-sm text-muted-foreground'>
						Chỉnh riêng từng sản phẩm trong danh sách đã chọn. Hiển thị cả giá cũ và đặc tả cũ để đối chiếu nhanh.
					</p>
					<p className='text-xs text-muted-foreground'>
						Giới hạn tối đa 10 sản phẩm mỗi lần.
					</p>
				</DialogHeader>

				<div className='flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6'>
					{isSubmitting && progressText && (
						<div className='rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary'>
							{progressText}
						</div>
					)}
					{isLoading && (
						<div className='rounded-2xl border border-dashed border-border/70 bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground'>
							Đang tải dữ liệu chi tiết sản phẩm...
						</div>
					)}
					{!isLoading && sortedProducts.length === 0 && (
						<div className='rounded-2xl border border-dashed border-border/70 bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground'>
							Không có sản phẩm nào để cập nhật.
						</div>
					)}
					{!isLoading && sortedProducts.map((product) => {
						const isExpanded = expandedProductIds.includes(product.id);
						const draft = drafts[product.id] || {};
						return (
							<div key={product.id} className='rounded-2xl border border-border/70 bg-background shadow-sm'>
								<button
									type='button'
									className='flex w-full items-start justify-between gap-4 px-4 py-3 text-left'
									onClick={() => {
										setExpandedProductIds((current) =>
											current.includes(product.id)
												? current.filter((id) => id !== product.id)
												: [...current, product.id]
										);
									}}
								>
									<div>
										<p className='text-sm font-semibold text-foreground'>{product.name}</p>
										<p className='text-xs text-muted-foreground'>SKU: {product.sku || 'N/A'} | ID: {product.id}</p>
									</div>
									<span className='mt-0.5 text-muted-foreground'>
										{isExpanded ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
									</span>
								</button>

								{isExpanded && (
									<div className='border-t px-4 py-4'>
										<div className='grid gap-4 lg:grid-cols-[1fr_1.15fr]'>
											<div className='space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4'>
												<p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Giá cũ</p>
												<p className='text-sm text-foreground'>Giá bán: {new Intl.NumberFormat('vi-VN').format(Number(product.price || 0))}</p>
												<p className='text-sm text-foreground'>Giá khuyến mãi: {new Intl.NumberFormat('vi-VN').format(Number(product.priceOnSale || 0))}</p>
												<div className='space-y-2'>
													<p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Đặc tả cũ</p>
													<div
														className='ck-content max-h-36 overflow-auto rounded-lg border bg-background p-3 text-sm text-foreground'
														dangerouslySetInnerHTML={{
															__html: product.summary?.trim()
																? product.summary
																: '<p class="text-muted-foreground">Không có đặc tả</p>',
														}}
													/>
												</div>
											</div>

											<div className='space-y-4 rounded-xl border border-border/70 bg-background p-4'>
												<div className='flex items-center justify-between gap-3'>
													<p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Giá mới</p>
													<p className='text-xs text-muted-foreground'>Nhập riêng từng sản phẩm</p>
												</div>
												<div className='grid gap-4 md:grid-cols-2'>
													<TextBox
														name={`price-${product.id}`}
														value={draft.price?.toString() || ''}
														onValueChanged={(value) => updateDraft(product.id, 'price', value)}
														type='number'
														label='Giá bán mới'
														placeholder='Bỏ trống nếu không đổi'
													/>
													<TextBox
														name={`priceOnSale-${product.id}`}
														value={draft.priceOnSale?.toString() || ''}
														onValueChanged={(value) => updateDraft(product.id, 'priceOnSale', value)}
														type='number'
														label='Giá khuyến mãi mới'
														placeholder='Bỏ trống nếu không đổi'
													/>
												</div>
												<div className='space-y-2'>
													<p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Đặc tả mới</p>
													<CKEditor
														value={String(draft.summary ?? '')}
														onChange={(value) => updateDraft(product.id, 'summary', value)}
														placeholder='Nhập đặc tả HTML...'
														minHeight={220}
														maxHeight={320}
														className='bulk-spec-editor'
													/>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				<DialogFooter className='border-t bg-muted/10 px-4 py-3 sm:px-6'>
					<Button type='button' variant='outline' onClick={onClose}>
						Hủy
					</Button>
					<Button type='button' onClick={handleApply} disabled={isLoading || isSubmitting}>
						{isSubmitting ? 'Đang áp dụng...' : 'Áp dụng thay đổi'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};